import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCustomer } from "@/contexts/CustomerContext";
import { TAXI_COMMANDER_LOGO } from "@/lib/brandAssets"; // v2.7.65+E173 · Dispatch System hero mark
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, User, ArrowLeft, Phone, Wrench } from "lucide-react";
import GoogleSignIn from "@/components/GoogleSignIn";
import PublicHoursBanner from "@/components/marketing/PublicHoursBanner";
import SmsConsent from "@/components/marketing/SmsConsent";
import VersionBadge from "@/components/VersionBadge";
import useSiteConfig from "@/hooks/useSiteConfig";
import api from "@/lib/api";

// v2.7.65+E152 · Fallback maintenance copy so the page never renders
// empty if /api/config is temporarily unreachable — we render the LOCK
// state, which is the safe default while the rollout is in flight.
const DEFAULT_MSG =
  "Rider account access is temporarily disabled for major system updates. "
  + "Please call (580) 219-4687 to book a ride, or use our Online Quick Booking form.";
const DEFAULT_DISPATCH_PHONE = "+15802194687";
const DISPATCH_DISPLAY = "(580) 219-4687";

function normalizeTelHref(raw) {
  const digits = String(raw || "").replace(/[^\d+]/g, "");
  return digits.startsWith("+") ? `tel:${digits}` : `tel:+${digits.replace(/^0+/, "")}`;
}

export default function CustomerLoginPage() {
  const { login, register } = useCustomer();
  const nav = useNavigate();
  const [tab, setTab] = useState("login");
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reg, setReg] = useState({ email: "", password: "", first_name: "", last_name: "", phone: "", home_address: "", date_of_birth: "" });
  // v2.7.65+E166 · TCPA-style SMS consent captured at rider signup; recorded
  // with the account so we can prove opt-in for outbound campaigns later.
  const [smsConsent, setSmsConsent] = useState(false);
  const { cfg } = useSiteConfig();
  const loginBgUrl = cfg?.global?.login_rider_bg_url || "/login-bg.jpg";

  // v2.8 · Rider login is permanently ENABLED. The old env-gated
  // "Rider Login Suppression" (E152) caused the login page to stay stuck on
  // the maintenance card in production whenever the RIDER_LOGIN_ENABLED env
  // var didn't propagate to the deployed app. Hard-enable so a redeploy always
  // shows the real login form; message/phone kept only for compatibility.
  const [authGate, setAuthGate] = useState({
    loading: false,
    enabled: true,
    message: DEFAULT_MSG,
    dispatchPhone: DEFAULT_DISPATCH_PHONE,
  });

  useEffect(() => {
    let cancelled = false;
    api.get("/config")
      .then(({ data }) => {
        if (cancelled) return;
        setAuthGate({
          loading: false,
          enabled: true,   // v2.8 · never gate the rider login form
          message: data?.rider_login_maintenance_msg || DEFAULT_MSG,
          dispatchPhone: data?.dispatch_phone || DEFAULT_DISPATCH_PHONE,
        });
      })
      .catch(() => {
        if (!cancelled) setAuthGate((s) => ({ ...s, loading: false }));
      });
    return () => { cancelled = true; };
  }, []);

  const submitLogin = async (e) => {
    e.preventDefault();
    if (!authGate.enabled) return;
    setBusy(true);
    try { await login(email, password); nav("/ride"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };
  const submitRegister = async (e) => {
    e.preventDefault();
    if (!authGate.enabled) return;
    setBusy(true);
    try {
      await register({
        ...reg,
        sms_consent: !!smsConsent,
        sms_consent_ts: smsConsent ? new Date().toISOString() : null,
      });
      nav("/ride");
    }
    catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && typeof detail === "object" && detail.code === "duplicate_account") {
        toast.error(detail.message, { duration: 7000 });
      } else {
        toast.error(typeof detail === "string" ? detail : "Sign-up failed");
      }
    }
    finally { setBusy(false); }
  };

  const telHref = normalizeTelHref(authGate.dispatchPhone);

  return (
    <div
      className="min-h-screen relative flex items-center justify-center p-5 overflow-hidden"
      style={{
        backgroundImage: `url("${loginBgUrl}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundColor: "#0c0d10",
      }}
      data-testid="rider-login-page-root"
    >
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(0,0,0,0.0) 30%, rgba(0,0,0,0.55) 100%)" }}/>

      <Link
        to="/"
        data-testid="rider-back-home"
        className="absolute top-4 left-4 z-20 inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 bg-zinc-950/70 backdrop-blur px-3 py-2 rounded-md border border-amber-400/30 uppercase tracking-wider font-bold"
      >
        <ArrowLeft className="h-3.5 w-3.5"/> Back to Home
      </Link>

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8" data-testid="rider-login-header">
          {/* v2.7.65+E173 · Uses the centralized Dispatch System crest
              when no CMS override is set, so the rider portal matches
              the admin sidebar + main login page branding. */}
          <img
            src={cfg?.global?.taxi_commander_logo_url || TAXI_COMMANDER_LOGO}
            alt="Buller Taxi Services · Dispatch System"
            className="mx-auto h-24 sm:h-28 object-contain select-none pointer-events-none drop-shadow-[0_4px_18px_rgba(250,204,21,0.35)]"
            data-testid="rider-login-commander-logo"
          />
          <h1 className="text-2xl font-extrabold tracking-tight mt-3">RIDER PORTAL</h1>
          <div className="text-amber-400 text-xs font-semibold tracking-[0.3em] mt-1">BULLER TAXI · BOOK A RIDE</div>
        </div>

        {/* v2.7.65+E152 · Rider Login Suppression maintenance overlay. */}
        {!authGate.loading && !authGate.enabled ? (
          <Card
            data-testid="rider-login-maintenance"
            className="bg-zinc-950/90 border-2 border-amber-400/60 p-6 backdrop-blur-xl shadow-2xl shadow-amber-500/20"
          >
            <div className="flex flex-col items-center text-center">
              <div className="h-16 w-16 rounded-full bg-amber-400/15 border border-amber-400/40 flex items-center justify-center mb-4">
                <Wrench className="h-7 w-7 text-amber-400"/>
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-extrabold">Scheduled Maintenance</div>
              <h2 className="text-lg font-bold text-zinc-100 mt-1.5">Rider Login Temporarily Offline</h2>
              <p
                data-testid="rider-login-maintenance-msg"
                className="text-sm text-zinc-300 mt-3 leading-relaxed max-w-sm"
              >
                {authGate.message}
              </p>

              <a
                href={telHref}
                data-testid="rider-login-call-dispatch"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold uppercase tracking-widest transition"
              >
                <Phone className="h-4 w-4"/> Call Dispatch · {DISPATCH_DISPLAY}
              </a>

              <Link
                to="/#book"
                data-testid="rider-login-quick-booking-cta"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 h-11 rounded-md border border-amber-400/40 bg-zinc-900 text-amber-300 hover:bg-amber-400/10 font-bold uppercase tracking-widest text-sm transition"
              >
                Use Online Quick Booking
              </Link>

              <div className="mt-5 text-[10px] text-zinc-500 uppercase tracking-widest">
                Guest bookings remain fully operational
              </div>
            </div>
          </Card>
        ) : (
          <Card className="bg-zinc-900/80 border-zinc-800 p-6 backdrop-blur-xl glow-amber">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid grid-cols-2 bg-zinc-950 mb-5">
                <TabsTrigger value="login" data-testid="r-tab-login">Sign In</TabsTrigger>
                <TabsTrigger value="signup" data-testid="r-tab-signup">Create Account</TabsTrigger>
              </TabsList>

              <GoogleSignIn redirectPath="/auth/callback"/>

              <TabsContent value="login">
                <form onSubmit={submitLogin} className="space-y-3">
                  <Input data-testid="r-email" type="email" placeholder="Email" value={email} onChange={e=>setEmail(e.target.value)} required className="bg-zinc-950 border-zinc-700 h-11"/>
                  <Input data-testid="r-password" type="password" placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} required className="bg-zinc-950 border-zinc-700 h-11"/>
                  <Button data-testid="r-login-btn" type="submit" disabled={busy} className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "SIGN IN"}</Button>
                </form>
              </TabsContent>
              <TabsContent value="signup">
                <form onSubmit={submitRegister} className="space-y-2.5">
                  <div className="grid grid-cols-2 gap-2">
                    <Input data-testid="rs-fname" placeholder="First name" value={reg.first_name} onChange={e=>setReg({...reg, first_name: e.target.value})} required className="bg-zinc-950 border-zinc-700 h-11"/>
                    <Input data-testid="rs-lname" placeholder="Last name" value={reg.last_name} onChange={e=>setReg({...reg, last_name: e.target.value})} required className="bg-zinc-950 border-zinc-700 h-11"/>
                  </div>
                  <Input data-testid="rs-email" type="email" placeholder="Email" value={reg.email} onChange={e=>setReg({...reg, email: e.target.value})} required className="bg-zinc-950 border-zinc-700 h-11"/>
                  <Input data-testid="rs-phone" placeholder="Phone" value={reg.phone} onChange={e=>setReg({...reg, phone: e.target.value})} required className="bg-zinc-950 border-zinc-700 h-11"/>
                  <Input data-testid="rs-address" placeholder="Home address (optional)" value={reg.home_address} onChange={e=>setReg({...reg, home_address: e.target.value})} className="bg-zinc-950 border-zinc-700 h-11"/>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500 ml-1">Date of birth (optional)</label>
                    <Input data-testid="rs-dob" type="date" value={reg.date_of_birth} onChange={e=>setReg({...reg, date_of_birth: e.target.value})} className="bg-zinc-950 border-zinc-700 h-11 mt-0.5"/>
                  </div>
                  <SmsConsent
                    value={smsConsent}
                    onChange={setSmsConsent}
                    testIdPrefix="rs-sms-consent"
                    className="pt-1"
                  />
                  <Input data-testid="rs-password" type="password" placeholder="Password" value={reg.password} onChange={e=>setReg({...reg, password: e.target.value})} required className="bg-zinc-950 border-zinc-700 h-11"/>
                  <Button data-testid="rs-signup-btn" type="submit" disabled={busy} className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "CREATE ACCOUNT"}</Button>
                </form>
              </TabsContent>
            </Tabs>
          </Card>
        )}

        {/* v2.8+E303 · Live business-hours banner (same schedule as the rider
            portal + homepage). Sits below the sign-in card. */}
        <PublicHoursBanner variant="login" />

        <Link to="/login" className="mt-4 block text-center text-[11px] text-zinc-500 hover:text-amber-400">Driver/Dispatch login →</Link>
      </div>
      <VersionBadge/>
    </div>
  );
}
